import { useState, useRef, useEffect } from 'react';
import styles from './PersonFilter.module.css';

interface Option {
  key: string;
  label: string;
}

interface Props {
  options: Option[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  mode: 'any' | 'all';
  onModeChange: (mode: 'any' | 'all') => void;
}

export function PersonFilter({ options, selected, onChange, mode, onModeChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const allSelected = selected.size === 0;
  const displayText = allSelected
    ? 'Person: All'
    : `Person: ${selected.size}`;

  const filtered = search
    ? options.filter(o =>
        o.key.toLowerCase().includes(search.toLowerCase()) ||
        o.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  function toggle(key: string) {
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange(next);
  }

  function clear() {
    onChange(new Set());
    setSearch('');
  }

  return (
    <div className={styles.root} ref={ref}>
      <button
        className={`${styles.trigger} ${!allSelected ? styles.triggerActive : ''}`}
        onClick={() => setOpen(!open)}
      >
        {displayText}
        <span className={styles.chevron}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <div className={styles.modeToggle}>
              <button
                className={`${styles.modeBtn} ${mode === 'any' ? styles.modeBtnActive : ''}`}
                onClick={() => onModeChange('any')}
              >
                Any
              </button>
              <button
                className={`${styles.modeBtn} ${mode === 'all' ? styles.modeBtnActive : ''}`}
                onClick={() => onModeChange('all')}
              >
                All
              </button>
            </div>
            {!allSelected && (
              <button className={styles.clearBtn} onClick={clear}>
                Clear
              </button>
            )}
          </div>
          <div className={styles.searchWrap}>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.optionsList}>
            {filtered.map(({ key, label }) => (
              <label key={key} className={styles.option}>
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => toggle(key)}
                  className={styles.checkbox}
                />
                {label}
              </label>
            ))}
            {filtered.length === 0 && (
              <div className={styles.empty}>No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
